<template>
  <div>
    <CCard>
      <CCardHeader>
        <slot name="header">
          <CIcon :name="tableIcon" /> {{ pageTitle }}
          <CButtonGroup
            class="float-right"
          >
            <CButton size="sm" color="primary" @click="onAddNew">
              <CIcon name="cil-plus" /> Add New
            </CButton>
            <CButton size="sm" color="success" @click="onRefresh">
              <CIcon name="cil-action-redo" /> Refresh
            </CButton>
          </CButtonGroup>
        </slot>
      </CCardHeader>
      <CCardBody>
        <PageFilter :filterData="filterData" :onFilterUpdate="onFilter" />
        <list-pagination
          :pageData="pageData"
          :perPageSelectData="perPageSelect"
          :entity="entity"
          containerClass="header-pagination"
          @pageChange="changePage"
        />
        <div class="lists-table">
          <CDataTable
            :hover="true"
            :striped="true"
            :border="true"
            :small="true"
            :fixed="true"
            :items="lists"
            :fields="fields"
            :dark="false"
            :loading="fetching"
            :clickableRows="true"
            addTableClasses="header-fixed"
            :sorter="{ external: true }"
            :sorterValue="sorterValue"
            @row-clicked="rowClicked"
            @update:sorter-value="updateSort"
          >
            <template #event_type="{item}">
              <td>
                {{ item.event_type_text }}
              </td>
            </template>
            <template #num_entries="{item}">
              <td class="text-center">
                <span class="d-block">{{ item.num_entries }}</span>
                <CLink @click="() => showEntries(item)">(View Details)</CLink>
              </td>
            </template>
            <template #is_retrieving_stake="{item}">
              <td class="text-center">
                <CBadge :color="item.is_retrieving_stake ? 'success' : 'danger'">
                  <CIcon :name="item.is_retrieving_stake ? 'cil-check-alt' : 'cil-x'" />
                </CBadge>
              </td>
            </template>
            <template #completed="{item}">
              <td class="text-center">
                <CBadge :color="item.is_completed ? 'success' : 'danger'">
                  {{ item.is_completed ? 'Completed' : 'Incomplete' }}
                </CBadge>
              </td>
            </template>
            <template #user="{item}">
              <td v-start-case>
                {{ item.user.name }}
              </td>
            </template>
            <template #action="{item}">
              <td>
                <CButtonGroup :vertical="true" size="sm">
                  <CButton v-if="item.num_entries > 0" color="success" @click="() => onGeneratePDF(item)">Generate PDF</CButton>
                  <CButton color="info" @click="() => onShowStakes(item)">Stakes</CButton>
                  <CButton color="primary" @click="() => onEditItem(item)">Edit</CButton>
                  <CButton color="danger" @click="() => onDeleteItem(item)">Delete</CButton>
                </CButtonGroup>
              </td>
            </template>
          </CDataTable>
        </div>
        <list-pagination
          :pageData="pageData"
          :perPageSelectData="perPageSelect"
          :entity="entity"
          containerClass="footer-pagination mt-2"
          @pageChange="changePage"
        />
      </CCardBody>
    </CCard>
    <Create
      v-if="newData.show"
      :key="newData.key"
    />
    <Update
      v-if="!!editData.data"
      :key="editData.key"
    />
    <Delete
      v-if="!!deleteData.data"
      :key="deleteData.key"
    />
    <Detail
      v-if="detailData.data"
      :key="detailData.key"
    />
    <EventStake
      v-if="eventStakeData.show"
      :key="eventStakeData.key"
    />
    <EventEntry
      v-if="eventEntryData.show"
      :key="eventEntryData.key"
    />
  </div>
</template>

<script>
import startCase from 'lodash/startCase'
import isObject from 'lodash/isObject'
import { mapActions, mapState } from 'vuex'
import store from './store'

import Create from './components/Create'
import Update from './components/Update'
import Delete from './components/Delete'
import Detail from './components/Detail'
import EventStake from './components/EventStake/EventStake'
import EventEntry from './components/EventEntry/EventEntry'

import { entity, tableIcon } from './entity'
import tableFields from './tableFields'
import filterData from './tableFilters'

export default {
  name: `${startCase(entity)}Lists`,
  title: startCase(entity),
  props: {
    showNew: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Create,
    Update,
    Delete,
    Detail,
    EventStake,
    EventEntry
  },
  data () {
    return {
      entity,
      tableIcon,
      pageTitle: startCase(entity),
      perPageSelect: {
        label: `${startCase(entity)} Per Page:`,
        options: [10, 15, 25, 50, 100],
        external: false
      },
      filterData
    }
  },
  computed: {
    ...mapState({
      user: state => state.session.loggedUser,
      lists: state => state[entity].lists,
      pageData: state => state[entity].pageData,
      sortData: state => state[entity].sortData,
      fetching: state => state[entity].isFetching,
      newData: state => state[entity].new,
      editData: state => state[entity].edit,
      deleteData: state => state[entity].delete,
      detailData: state => state[entity].detail,
      eventStakeData: state => state[entity].eventStakes,
      eventEntryData: state => state[entity].eventEntries
    }),
    sorterValue () {
      const { column, direction } = this.sortData
      return {
        column,
        asc: direction === 'asc'
      }
    },
    fields () {
      if (!this.user.is_admin) {
        return tableFields.filter(e => !isObject(e) || e.key !== 'user')
      }
      return tableFields
    }
  },
  methods: {
    ...mapActions({
      loadLists: `${entity}/loadLists`,
      changePage: `${entity}/changePage`,
      changeSort: `${entity}/changeSort`
    }),
    onAddNew () {
      this.$store.commit(`${entity}/ADD_NEW`, true)
    },
    async onFilter (filters) {
      await this.$store.commit(`${entity}/EDIT_FILTERS`, filters)
      this.loadLists()
    },
    onRefresh () {
      this.loadLists()
    },
    onEditItem (item) {
      this.$store.commit(`${entity}/EDIT_DATA`, item)
    },
    onDeleteItem (item) {
      this.$store.commit(`${entity}/DELETE_DATA`, item)
    },
    onPageChange (currentPage) {
      this.changePage({ currentPage })
    },
    onPaginationChange (perPage) {
      this.changePage({ perPage })
    },
    rowClicked (item, idx, key) {
      const byPassRows = ['action', 'num_entries']
      if (!byPassRows.includes(key)) {
        this.$store.commit(`${entity}/DETAIL_DATA`, item)
      }
    },
    updateSort ({ asc, column }) {
      this.changeSort({ column, direction: asc ? 'asc' : 'desc' })
    },
    onGeneratePDF (item) {
      const { VUE_APP_PRINT_URL } = process.env
      window.location.href = `${VUE_APP_PRINT_URL}/${item.id}`
    },
    onShowStakes (event) {
      this.$store.commit(`${entity}/SHOW_EVENT_STAKES`, { event })
    },
    showEntries (event) {
      this.$store.commit(`${entity}/SHOW_EVENT_ENTRIES`, { event })
    }
  },
  beforeMount () {
    if (!this.$store.state[entity]) {
      this.$store.registerModule(entity, store)
    }
  },
  mounted () {
    this.loadLists()
  }
}
</script>
